import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { TAX_RATE, detectType, itemClientPrice, itemTrueCost, sectionClientTotal, sectionTrueCost } from '../lib/estimateFinancials'
import { buildPDFHTML } from '../lib/estimatePDF'
import MetricsTab from '../components/estimate/MetricsTab'
import AddMaterialModal, { UNITS } from '../components/estimate/AddMaterialModal'
import SendEstimateModal from '../components/estimate/SendEstimateModal'
import MaterialDropdown from '../components/estimate/MaterialDropdown'
import QuickAddClientModal from '../components/QuickAddClientModal'
import { useIsMobile } from '../components/Layout'

// ─── Constants ────────────────────────────────────────────────────────────────
const MARKUP_OPTIONS = [
  { label: '2x (100%)', value: 100 },
  { label: '1.5x (50%)', value: 50 },
  { label: '1.25x (25%)', value: 25 },
  { label: 'No markup (0%)', value: 0 },
  { label: 'Custom %', value: 'custom' },
]

const SECTION_PRESETS = [
  'Demo & Site Prep', 'Xeriscape', 'Sod', 'Pavers', 'Planter',
  'Irrigation', 'Tree Removal', 'Tree Pruning', 'Stump Grinding',
  'Rock / DG', 'Plants & Trees', 'Labor', 'Equipment', 'Overhead', 'General',
]

const VISIBILITY_OPTIONS = [
  { key: 'show_section_subtotals', label: 'Section subtotals' },
  { key: 'show_unit_costs', label: 'Line item unit costs' },
  { key: 'show_description', label: 'Scope / description' },
  { key: 'show_client_notes', label: 'Client notes' },
  { key: 'show_payment_terms', label: 'Payment terms' },
  { key: 'show_deposit_info', label: 'Deposit requirement' },
  { key: 'show_expiry_date', label: 'Expiry date' },
]

let _id = 0
function uid() { return `_${++_id}_${Date.now()}` }

// ─── Blank constructors ────────────────────────────────────────────────────────
function blankItem() {
  return {
    _id: uid(), description: '', material_id: null,
    qty: '1', unit: 'ea', unit_cost: '',
    markup_pct: 100, markup_type: 'preset', custom_markup: '',
    baked_in: false, notes: '', item_type: 'Material',
  }
}

function blankSection(name = '') {
  const isOverhead = name.toLowerCase().includes('overhead')
  return {
    _id: uid(), name, section_description: '',
    items: [blankItem()],
    client_optional: false, client_selected: true,
    is_overhead: isOverhead,
    // For optional sections: mutual exclusion group
    option_group: null,
  }
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function EstimateBuilder() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const isEdit = Boolean(id)
  const { profile } = useAuth()
  const isMobile = useIsMobile()

  const [clients, setClients] = useState([])
  const [materials, setMaterials] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [activeTab, setActiveTab] = useState('build') // 'build' | 'metrics'
  const [showAddClient, setShowAddClient] = useState(false)

  const [clientId, setClientId] = useState('')
  const [quoteNumber, setQuoteNumber] = useState('')
  const [description, setDescription] = useState('')
  const [clientNotes, setClientNotes] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [paymentTerms, setPaymentTerms] = useState('')
  const [status, setStatus] = useState('draft')
  const [applyTax, setApplyTax] = useState(true)
  const [depositPct, setDepositPct] = useState('50')
  const [estimatedHours, setEstimatedHours] = useState('')
  const [expiryDate, setExpiryDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]
  })
  const [visibility, setVisibility] = useState({
    show_section_subtotals: true, show_unit_costs: false,
    show_description: true, show_client_notes: true, show_payment_terms: true,
    show_deposit_info: true, show_expiry_date: true,
  })
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false)
  const [sections, setSections] = useState([])
  const [showSectionPicker, setShowSectionPicker] = useState(false)
  const [customSectionName, setCustomSectionName] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [showTemplateLoad, setShowTemplateLoad] = useState(false)
  const [showAddMaterial, setShowAddMaterial] = useState(false)
  const [showSendModal, setShowSendModal] = useState(false)
  const [generatingProposal, setGeneratingProposal] = useState(false)

  useEffect(() => { fetchInitial() }, [])

  async function fetchInitial() {
    const [clientsRes, materialsRes, countRes, templatesRes] = await Promise.all([
      supabase.from('clients').select('id, name, address, phone, email, active').order('name'),
      supabase.from('materials').select('*').order('category').order('name'),
      supabase.from('estimates').select('id', { count: 'exact', head: true }),
      supabase.from('estimate_templates').select('*').order('name'),
    ])
    if (clientsRes.error) setError('Could not load clients: ' + clientsRes.error.message)
    else setClients((clientsRes.data || []).filter(c => c.active !== false))
    setMaterials(materialsRes.data || [])
    setTemplates(templatesRes.data || [])
    const nextNum = (countRes.count || 0) + 1
    setQuoteNumber(String(nextNum).padStart(3, '0'))
    if (isEdit) {
      await loadEstimate()
    } else {
      const prefillClient = searchParams.get('client')
      if (prefillClient) setClientId(prefillClient)
      const prefillNotes = searchParams.get('notes')
      if (prefillNotes) setInternalNotes(decodeURIComponent(prefillNotes))
      const siteVisitId = searchParams.get('site_visit')
      if (siteVisitId) {
        // Seed one section per site-visit task so structure carries forward
        // into pricing instead of collapsing to a single notes blob.
        const { data: visitItems } = await supabase.from('site_visit_items')
          .select('*').eq('site_visit_id', siteVisitId).order('sort_order').order('created_at')
        if (visitItems && visitItems.length > 0) {
          setSections(visitItems.map(task => ({
            ...blankSection(task.title?.trim() || 'Untitled Task'),
            section_description: task.description || '',
          })))
        }
      }
    }
    setLoading(false)
  }

  async function loadEstimate() {
    const { data, error } = await supabase.from('estimates').select('*').eq('id', id).single()
    if (error || !data) { setError('Could not load estimate.'); return }
    setClientId(data.client_id || '')
    setDescription(data.description || '')
    setClientNotes(data.client_notes || '')
    setInternalNotes(data.notes || '')
    setStatus(data.status || 'draft')
    setQuoteNumber(data.quote_number || '')
    setDepositPct(String(data.deposit_pct || 50))
    if (data.expiry_date) setExpiryDate(data.expiry_date)
    setPaymentTerms(data.payment_terms_text || '')
    if (data.visibility_settings) { try { setVisibility(JSON.parse(data.visibility_settings)) } catch (e) {} }
    if (data.sections_data) {
      try {
        const parsed = JSON.parse(data.sections_data)
        setSections(parsed.sections || [])
        if (typeof parsed.applyTax === 'boolean') setApplyTax(parsed.applyTax)
        if (parsed.estimatedHours) setEstimatedHours(parsed.estimatedHours)
      } catch (e) {}
    }
  }

  function toggleVisibility(key) { setVisibility(v => ({ ...v, [key]: !v[key] })) }

  // ── Sections ──────────────────────────────────────────────────────────────
  function addSection(name) {
    if (!name.trim()) return
    setSections(prev => [...prev, blankSection(name.trim())])
    setShowSectionPicker(false); setCustomSectionName('')
  }
  function removeSection(sId) { setSections(prev => prev.filter(s => s._id !== sId)) }
  function updateSection(sId, field, value) { setSections(prev => prev.map(s => s._id === sId ? { ...s, [field]: value } : s)) }
  function moveSection(sId, dir) {
    setSections(prev => {
      const idx = prev.findIndex(s => s._id === sId)
      const next = [...prev]
      const swap = idx + dir
      if (swap < 0 || swap >= next.length) return prev
      ;[next[idx], next[swap]] = [next[swap], next[idx]]
      return next
    })
  }
  function duplicateSection(sId) {
    setSections(prev => {
      const idx = prev.findIndex(s => s._id === sId)
      if (idx === -1) return prev
      const orig = prev[idx]
      const copy = { ...orig, _id: uid(), name: orig.name + ' (Copy)', items: orig.items.map(i => ({ ...i, _id: uid() })) }
      const next = [...prev]; next.splice(idx + 1, 0, copy); return next
    })
  }

  // ── Items ─────────────────────────────────────────────────────────────────
  function addItem(sId) { setSections(prev => prev.map(s => s._id === sId ? { ...s, items: [...s.items, blankItem()] } : s)) }
  function removeItem(sId, iId) {
    setSections(prev => prev.map(s => {
      if (s._id !== sId) return s
      const items = s.items.filter(i => i._id !== iId)
      return { ...s, items: items.length ? items : [blankItem()] }
    }))
  }
  function updateItem(sId, iId, field, value) {
    setSections(prev => prev.map(s => {
      if (s._id !== sId) return s
      return {
        ...s, items: s.items.map(i => {
          if (i._id !== iId) return i
          const updated = { ...i, [field]: value }
          if (field === 'item_type') updated.type_locked = true
          if (field === 'material_id' && value) {
            const mat = materials.find(m => m.id === value)
            if (mat) { if (!updated.description) updated.description = mat.name; updated.unit_cost = String(mat.unit_cost); updated.unit = mat.unit; updated.item_type = 'Material'; updated.type_locked = true }
          }
          if (field === 'description' && !updated.type_locked) {
            const autoType = detectType({ ...updated, description: value }, s.name)
            if (!updated.item_type || updated.item_type === 'Material') updated.item_type = autoType
          }
          return updated
        })
      }
    }))
  }

  // ── Templates ─────────────────────────────────────────────────────────────
  async function saveTemplate() {
    if (!templateName.trim()) return setError('Template name is required.')
    const { data, error } = await supabase.from('estimate_templates').insert([{ name: templateName.trim(), sections_data: JSON.stringify({ sections, applyTax }) }]).select().single()
    if (error) return setError('Failed to save template: ' + error.message)
    setTemplates(prev => [...prev, data]); setTemplateName(''); setSuccess('Template saved.'); setTimeout(() => setSuccess(null), 2000)
  }
  function loadTemplate(tpl) {
    try {
      const parsed = JSON.parse(tpl.sections_data)
      setSections((parsed.sections || []).map(s => ({ ...s, _id: uid(), items: (s.items || []).map(i => ({ ...i, _id: uid() })) })))
      if (typeof parsed.applyTax === 'boolean') setApplyTax(parsed.applyTax)
      setShowTemplateLoad(false); setSuccess('Template loaded.'); setTimeout(() => setSuccess(null), 2000)
    } catch (e) { setError('Failed to load template.') }
  }
  async function deleteTemplate(tplId) {
    if (!window.confirm('Delete this template?')) return
    await supabase.from('estimate_templates').delete().eq('id', tplId)
    setTemplates(prev => prev.filter(t => t.id !== tplId))
  }

  // ── Totals & Metrics ──────────────────────────────────────────────────────

  // Step 1: Calculate overhead total from overhead sections
  const overheadSections = sections.filter(s => s.is_overhead)
  const overheadTotal = overheadSections.reduce((s, sec) => s + sectionTrueCost(sec), 0)

  // Step 2: Calculate total labor hours across all non-overhead sections
  // We need this BEFORE calculating client totals so we can spread overhead into labor prices
  const totalLaborHours = (() => {
    let hrs = 0
    for (const sec of sections) {
      if (sec.is_overhead) continue
      for (const item of sec.items) {
        if (detectType(item, sec.name) === 'Labor') hrs += parseFloat(item.qty || 0)
      }
    }
    return hrs
  })()

  // Step 3: Overhead per labor hour — spread across all labor line items
  // This is what gets ADDED to each labor item's client price
  const overheadPerLaborHour = totalLaborHours > 0 ? overheadTotal / totalLaborHours : 0

  // Step 4: Client-facing totals — overhead IS included via labor spread
  const baseSections = sections.filter(s => !s.is_overhead && !s.client_optional)
  const optionalSelected = sections.filter(s => !s.is_overhead && s.client_optional && s.client_selected)
  // Pass overheadPerLaborHour so labor items get overhead baked into their client price
  const baseSubtotal = baseSections.reduce((s, sec) => s + sectionClientTotal(sec, overheadPerLaborHour), 0)
  const optionalSubtotal = optionalSelected.reduce((s, sec) => s + sectionClientTotal(sec, overheadPerLaborHour), 0)
  const subtotal = baseSubtotal + optionalSubtotal
  const tax = applyTax ? subtotal * TAX_RATE : 0
  const grandTotal = subtotal + tax

  // Step 5: True cost = everything it actually costs (materials + labor + overhead)
  const trueCost = sections.reduce((s, sec) => s + sectionTrueCost(sec), 0)
  const grossProfit = grandTotal - trueCost
  const actualMargin = grandTotal > 0 ? (grossProfit / grandTotal * 100) : 0
  const depositAmt = grandTotal * (parseFloat(depositPct || 50) / 100)
  const balanceDue = grandTotal - depositAmt

  // Cost by type for metrics
  const costByType = { Material: 0, Labor: 0, Equipment: 0, Other: 0, Overhead: 0 }
  for (const sec of sections) {
    for (const item of sec.items) {
      const type = detectType(item, sec.name)
      costByType[type] = (costByType[type] || 0) + itemTrueCost(item)
    }
  }

  // Loaded labor cost = base labor true cost + overhead (what the job truly costs you)
  const loadedLaborCost = costByType.Labor + overheadTotal

  // ── Save ──────────────────────────────────────────────────────────────────
  async function saveEstimate(newStatus, { openSendModal = true } = {}) {
    setError(null)
    const warnings = []
    if (!clientId) warnings.push('no client selected')
    if (sections.length === 0) warnings.push('no sections added')
    if (!quoteNumber.trim()) warnings.push('quote number is blank')
    if (warnings.length > 0) {
      if (!window.confirm(`This estimate has: ${warnings.join(', ')}. Save anyway?`)) return null
    }
    setSaving(true)
    const saveStatus = newStatus || status
    const payload = {
      client_id: clientId || null, description: description.trim() || null,
      client_notes: clientNotes.trim() || null,
      notes: internalNotes.trim() || null,
      scope_text: description.trim() || null,
      title: selectedClient ? `${selectedClient.name} — Quote #${quoteNumber}` : `Quote #${quoteNumber}`,
      status: saveStatus, quote_number: quoteNumber, suggested_price: grandTotal,
      labor_cost: costByType.Labor, materials_cost: costByType.Material + costByType.Equipment,
      total_cost: trueCost, markup_pct: 0,
      deposit_pct: parseFloat(depositPct) || 50, deposit_amount: depositAmt,
      expiry_date: expiryDate || null, visibility_settings: JSON.stringify(visibility),
      payment_terms_text: paymentTerms.trim() || null,
      sections_data: JSON.stringify({ sections, applyTax, estimatedHours }),
      job_type: 'Custom', estimated_hours: parseFloat(estimatedHours) || 0, equipment_cost: costByType.Equipment,
      // Only stamped on initial creation -- never overwritten by a later edit save,
      // since edit-mode has no ?site_visit= param to read from.
      ...(isEdit ? {} : { site_visit_id: searchParams.get('site_visit') || null }),
    }
    let err, savedId = id
    if (isEdit) {
      const res = await supabase.from('estimates').update(payload).eq('id', id)
      err = res.error
    } else {
      const res = await supabase.from('estimates').insert([payload]).select('id').single()
      err = res.error; if (res.data) savedId = res.data.id
    }
    if (err) { setError('Save failed: ' + err.message); setSaving(false); return null }
    if (!isEdit && savedId) {
      const siteVisitId = searchParams.get('site_visit')
      if (siteVisitId) {
        // .eq('status', 'Open') guards against a second Save on this same
        // /estimates/new page (e.g. after Generate Proposal) re-pointing an
        // already-converted site visit at a duplicate estimate.
        const { error: svErr } = await supabase.from('site_visits').update({
          status: 'Converted', converted_to_estimate_id: savedId,
        }).eq('id', siteVisitId).eq('status', 'Open')
        if (svErr) setError('Estimate saved, but could not mark the source site visit as converted: ' + svErr.message)
      }
    }
    setSaving(false); setSuccess('Saved.'); setTimeout(() => setSuccess(null), 2000)
    if (newStatus === 'sent' && openSendModal) setShowSendModal(true)
    else if (!newStatus) setTimeout(() => navigate('/estimates'), 800)
    return savedId
  }

  async function convertToJob() {
    setError(null)
    const savedId = await saveEstimate('approved')
    if (!savedId) return
    const params = new URLSearchParams()
    if (clientId) params.set('client', clientId)
    params.set('estimate_price', grandTotal.toFixed(2))
    params.set('estimate', savedId)
    // Pass ALL planned values so Jobs.js can store them on the job record
    params.set('planned_material_cost', costByType.Material.toFixed(2))
    params.set('planned_labor_cost', (costByType.Labor + overheadTotal).toFixed(2))
    params.set('planned_labor_hours', (parseFloat(estimatedHours) || totalLaborHours).toFixed(2))
    params.set('planned_overhead_cost', overheadTotal.toFixed(2))
    params.set('planned_total_cost', trueCost.toFixed(2))
    params.set('planned_revenue', grandTotal.toFixed(2))
    params.set('planned_gross_profit', grossProfit.toFixed(2))
    params.set('planned_margin', actualMargin.toFixed(2))
    if (description) params.set('scope_text', encodeURIComponent(description))
    navigate(`/jobs/new?${params.toString()}`)
  }

  function printPDF() {
    const pdfHTML = buildPDFHTML({ meta: { description }, sections, client: clients.find(c => c.id === clientId), visibility, depositPct, applyTax, expiryDate, clientNotes, quoteNumber })
    const win = window.open('', '_blank')
    if (!win) { alert('Please allow popups.'); return }
    win.document.write(pdfHTML); win.document.close()
    setTimeout(() => win.print(), 600)
  }

  function downloadPDF() {
    const pdfHTML = buildPDFHTML({ meta: { description }, sections, client: clients.find(c => c.id === clientId), visibility, depositPct, applyTax, expiryDate, clientNotes, quoteNumber })
    const blob = new Blob([pdfHTML], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `TLC_Proposal_${quoteNumber}_${(clients.find(c => c.id === clientId)?.name || 'Client').replace(/\s+/g, '_')}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Creates a customer-safe `proposals` row (+ one `proposal_options` row per optional
  // section) snapshotted from the current estimate state, then opens the preview.
  // See supabase/migrations/20260711000000_create_proposals_and_proposal_options.sql
  // for the target schema and Docs/QUOTE_TO_CASH.md "Proposal Rules" for the doctrine.
  async function generateProposal() {
    setError(null)
    if (!clientId) { setError('Select a client before generating a proposal.'); return }
    if (sections.length === 0) { setError('Add at least one section before generating a proposal.'); return }

    setGeneratingProposal(true)

    // Make sure the source estimate is saved first — the proposal snapshots from it
    // and proposals.estimate_id is a required FK. Pass the current status through so
    // saveEstimate() doesn't treat this as a normal "Save Draft" and navigate away.
    // openSendModal: false — an estimate already marked 'sent' would otherwise pop
    // open the unrelated Send Estimate modal as a side effect of generating a proposal.
    const savedEstimateId = await saveEstimate(status || 'draft', { openSendModal: false })
    if (!savedEstimateId) { setGeneratingProposal(false); return }

    // Proposal-only sections total (base = required, non-optional, non-overhead sections).
    // Optional add-ons are NOT included here — per QUOTE_TO_CASH.md, an add-on only becomes
    // part of the contract total once explicitly accepted (Session 3.4, not this session).
    const taxRateForProposal = applyTax ? TAX_RATE : 0
    const taxAmountForProposal = baseSubtotal * taxRateForProposal
    const selectedContractTotal = baseSubtotal // nothing accepted yet on a fresh proposal
    const depositAmountForProposal = (selectedContractTotal + taxAmountForProposal) * (parseFloat(depositPct || 50) / 100)

    const { count } = await supabase.from('proposals').select('id', { count: 'exact', head: true })
    const proposalNumber = `PROP-${String((count || 0) + 1).padStart(3, '0')}`

    // Regenerating from an already-proposed estimate must supersede the prior current
    // version rather than leaving two rows both marked is_current_version — see
    // Docs/DECISIONS_LOG.md FA-012 and QUOTE_TO_CASH.md "Proposal Versions". The old row
    // has to be marked superseded BEFORE the new row is inserted: proposals_one_current_per_group
    // is a partial unique index on is_current_version, so two current rows in the same
    // version group at once would violate it once root_proposal_id links them.
    const { data: existingCurrent } = await supabase
      .from('proposals')
      .select('id, root_proposal_id, version_number')
      .eq('estimate_id', savedEstimateId)
      .eq('is_current_version', true)
      .maybeSingle()

    const rootProposalId = existingCurrent ? (existingCurrent.root_proposal_id || existingCurrent.id) : null
    const versionNumber = existingCurrent ? existingCurrent.version_number + 1 : 1

    if (existingCurrent) {
      const { error: supersedeErr } = await supabase.from('proposals').update({ is_current_version: false }).eq('id', existingCurrent.id)
      if (supersedeErr) { setError('Failed to supersede the previous proposal version: ' + supersedeErr.message); setGeneratingProposal(false); return }
    }

    const proposalPayload = {
      estimate_id: savedEstimateId,
      client_id: clientId,
      root_proposal_id: rootProposalId,
      version_number: versionNumber,
      proposal_number: proposalNumber,
      title: selectedClient ? `${selectedClient.name} — Proposal #${proposalNumber}` : `Proposal #${proposalNumber}`,
      status: 'draft',
      // Snapshot of the estimate's sections + a couple of estimate-level text fields
      // (description, client notes) that the proposals table doesn't have dedicated
      // columns for yet — bundled into this JSON blob rather than adding new columns
      // outside the approved Session 3.1 schema. Flagged in the handoff as a judgment call.
      sections_data: JSON.stringify({ sections, applyTax, estimatedHours, description: description.trim() || null, clientNotes: clientNotes.trim() || null }),
      visibility_settings: JSON.stringify(visibility),
      base_scope_total: baseSubtotal,
      selected_contract_total: selectedContractTotal,
      tax_rate: taxRateForProposal,
      tax_amount: taxAmountForProposal,
      deposit_pct: parseFloat(depositPct) || 50,
      deposit_amount: depositAmountForProposal,
      payment_terms_text: paymentTerms.trim() || null,
      expires_at: expiryDate || null,
      created_by: profile?.id || null,
    }

    const { data: proposal, error: proposalErr } = await supabase.from('proposals').insert([proposalPayload]).select().single()
    if (proposalErr) { setError('Failed to generate proposal: ' + proposalErr.message); setGeneratingProposal(false); return }

    const optionalSections = sections.filter(s => !s.is_overhead && s.client_optional)
    if (optionalSections.length > 0) {
      const optionRows = optionalSections.map(sec => ({
        proposal_id: proposal.id,
        section_ref: sec._id,
        label: sec.name,
        price: sectionClientTotal(sec, overheadPerLaborHour),
        status: 'offered',
      }))
      const { error: optionsErr } = await supabase.from('proposal_options').insert(optionRows)
      if (optionsErr) { setError('Proposal was created but optional add-ons failed to save: ' + optionsErr.message); setGeneratingProposal(false); return }
    }

    setGeneratingProposal(false)
    navigate(`/proposals/${proposal.id}`)
  }

  const selectedClient = clients.find(c => c.id === clientId)
  const visibleCount = VISIBILITY_OPTIONS.filter(o => visibility[o.key]).length

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#6b7c6b' }}>Loading...</div>

  return (
    <div style={s.page}>
      {showAddMaterial && (
        <AddMaterialModal
          onClose={() => setShowAddMaterial(false)}
          onSaved={newMat => { setMaterials(prev => [...prev, newMat].sort((a, b) => a.name.localeCompare(b.name))); setShowAddMaterial(false); setSuccess('Material added!'); setTimeout(() => setSuccess(null), 2000) }}
        />
      )}
      {showSendModal && (
        <SendEstimateModal client={selectedClient} quoteNumber={quoteNumber} grandTotal={grandTotal} expiryDate={expiryDate} onClose={() => setShowSendModal(false)} onPreviewPDF={printPDF} />
      )}
      {showAddClient && (
        <QuickAddClientModal
          onClose={() => setShowAddClient(false)}
          onCreated={(newClient) => {
            setClients(prev => [...prev, newClient].sort((a, b) => a.name.localeCompare(b.name)))
            setClientId(newClient.id)
            setShowAddClient(false)
          }}
        />
      )}

      {/* TOP BAR */}
      <div style={s.topBar}>
        <div style={s.topBarLeft}>
          <button style={s.backBtn} onClick={() => navigate('/estimates')}>← Back</button>
          <div>
            <h1 style={s.title}>{isEdit ? `Edit Estimate #${quoteNumber}` : 'New Estimate'}</h1>
            <p style={s.sub}>Create a detailed estimate, then generate a customer-facing proposal</p>
          </div>
        </div>
        <div style={s.topBarRight}>
          <button style={s.pdfBtn} onClick={printPDF}>Preview PDF</button>
          <button style={s.downloadBtn} onClick={downloadPDF}>⬇ Download PDF</button>
          <button
            style={s.generateProposalBtn}
            onClick={generateProposal}
            disabled={generatingProposal || saving}
            title={
              optionalSelected.length > 0
                ? `Proposal starts at the base total ($${baseSubtotal.toFixed(2)}) — the ${optionalSelected.length} optional section(s) checked here are offered separately on the proposal, not pre-included, even though they're currently added into the $${grandTotal.toFixed(2)} shown on this screen.`
                : 'Create a customer-safe proposal snapshot from this estimate'
            }
          >
            {generatingProposal ? 'Generating...' : '📄 Generate Proposal'}
          </button>
          <button style={s.saveDraftBtn} onClick={() => saveEstimate()} disabled={saving}>{saving ? 'Saving...' : 'Save Draft'}</button>
          <button style={s.sendNowBtn} onClick={() => saveEstimate('sent')} disabled={saving}>Send to Client...</button>
        </div>
      </div>

      {error && <div style={s.errorBox}>{error}<button style={s.dismissBtn} onClick={() => setError(null)}>✕</button></div>}
      {success && <div style={s.successBox}>{success}</div>}

      {/* TABS */}
      <div style={s.tabs}>
        <button style={{ ...s.tab, ...(activeTab === 'build' ? s.tabActive : {}) }} onClick={() => setActiveTab('build')}>📋 Build Estimate</button>
        <button style={{ ...s.tab, ...(activeTab === 'metrics' ? s.tabActive : {}) }} onClick={() => setActiveTab('metrics')}>
          📊 Job Metrics
          {activeTab !== 'metrics' && <span style={{ ...s.metricsPill, ...(actualMargin < 35 ? { background: '#fef2f2', color: '#dc2626' } : {}) }}>{actualMargin.toFixed(0)}% margin</span>}
        </button>
      </div>

      {activeTab === 'metrics' ? (
        <MetricsTab
          sections={sections}
          grandTotal={grandTotal}
          tax={tax}
          trueCost={trueCost}
          costByType={costByType}
          overheadTotal={overheadTotal}
          overheadPerLaborHour={overheadPerLaborHour}
          totalLaborHours={totalLaborHours}
          loadedLaborCost={loadedLaborCost}
          estimatedHours={parseFloat(estimatedHours) || 0}
          loadedLaborRate={parseFloat(estimatedHours) > 0 ? costByType.Labor / parseFloat(estimatedHours) : 0}
        />
      ) : (
        <div style={{ ...s.twoCol, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
          <div style={s.leftCol}>

            {/* Client Info */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Client Information</div>
              <div style={{ ...s.clientGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={s.label}>Client Name</label>
                  <select
                    style={s.input}
                    value={clientId}
                    onChange={e => {
                      if (e.target.value === '__add_new__') { setShowAddClient(true); return }
                      setClientId(e.target.value)
                    }}
                  >
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    <option value="__add_new__">+ Add New Client</option>
                  </select>
                  {selectedClient && (
                    <div style={s.clientAutoFill}>
                      {selectedClient.address && <span style={s.clientDetail}>📍 {selectedClient.address}</span>}
                      {selectedClient.phone && <span style={s.clientDetail}>📞 {selectedClient.phone}</span>}
                      {selectedClient.email && <span style={s.clientDetail}>✉ {selectedClient.email}</span>}
                    </div>
                  )}
                </div>
                <div><label style={s.label}>Quote #</label><input style={s.input} value={quoteNumber} onChange={e => setQuoteNumber(e.target.value)} /></div>
                <div><label style={s.label}>Valid Until</label><input style={s.input} type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} /></div>
                <div><label style={s.label}>Status</label><select style={s.input} value={status} onChange={e => setStatus(e.target.value)}><option value="draft">Draft</option><option value="sent">Sent</option><option value="approved">Approved</option><option value="declined">Declined</option></select></div>
                <div><label style={s.label}>Est. Crew Hours</label><input style={s.input} type="number" min="0" step="0.5" value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)} placeholder="0" /></div>
                <div style={{ gridColumn: 'span 2' }}><label style={s.label}>Scope of Work</label><textarea style={{ ...s.input, minHeight: '72px', resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of the scope..." /></div>
              </div>
            </div>

            {/* Toolbar */}
            <div style={s.toolbar}>
              <div style={{ position: 'relative' }}>
                <button style={s.toolBtn} onClick={() => { setShowTemplateLoad(!showTemplateLoad); setShowVisibilityMenu(false) }}>📋 Templates {templates.length > 0 ? `(${templates.length})` : ''}</button>
                {showTemplateLoad && (
                  <div style={s.dropdown}>
                    <div style={s.dropdownTitle}>Load Template</div>
                    {templates.length === 0 ? <div style={s.dropdownEmpty}>No templates yet</div>
                      : templates.map(tpl => (
                        <div key={tpl.id} style={s.dropdownRow}>
                          <span style={s.dropdownLabel}>{tpl.name}</span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button style={s.dropdownBtn} onClick={() => loadTemplate(tpl)}>Load</button>
                            <button style={{ ...s.dropdownBtn, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => deleteTemplate(tpl.id)}>✕</button>
                          </div>
                        </div>
                      ))}
                    <div style={{ padding: '8px 14px', borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input style={{ ...s.input, fontSize: '12px', padding: '6px 10px', flex: 1 }} value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Save current as template..." onKeyDown={e => e.key === 'Enter' && saveTemplate()} />
                        <button style={s.saveDraftBtn} onClick={saveTemplate}>Save</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ position: 'relative', marginLeft: 'auto' }}>
                <button style={s.toolBtn} onClick={() => { setShowVisibilityMenu(!showVisibilityMenu); setShowTemplateLoad(false) }}>👁 Client View ({visibleCount}/{VISIBILITY_OPTIONS.length})</button>
                {showVisibilityMenu && (
                  <div style={{ ...s.dropdown, right: 0, left: 'auto', minWidth: '240px' }}>
                    <div style={s.dropdownTitle}>What client sees on PDF</div>
                    {VISIBILITY_OPTIONS.map(opt => (
                      <label key={opt.key} style={s.visRow}><input type="checkbox" checked={visibility[opt.key]} onChange={() => toggleVisibility(opt.key)} /><span style={{ fontSize: '13px', color: '#374151' }}>{opt.label}</span></label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Service Items */}
            <div style={s.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={s.sectionTitle}>Service Items</div>
                <button style={s.addMaterialQuickBtn} onClick={() => setShowAddMaterial(true)}>+ Add to Material List</button>
              </div>

              {/* Optional pricing summary */}
              {sections.some(s => s.client_optional) && (
                <div style={s.optionalBanner}>
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div><span style={{ fontSize: '12px', color: '#6b7c6b' }}>Base price: </span><strong style={{ color: '#1a2e1a' }}>${(baseSubtotal + (applyTax ? baseSubtotal * TAX_RATE : 0)).toFixed(2)}</strong></div>
                    <div style={{ color: '#9ca3af' }}>+</div>
                    <div><span style={{ fontSize: '12px', color: '#2563eb' }}>Optional add-ons: </span><strong style={{ color: '#2563eb' }}>${optionalSubtotal.toFixed(2)}</strong></div>
                    <div style={{ color: '#9ca3af' }}>=</div>
                    <div><span style={{ fontSize: '12px', color: '#6b7c6b' }}>Total with options: </span><strong style={{ color: '#1a2e1a' }}>${grandTotal.toFixed(2)}</strong></div>
                  </div>
                </div>
              )}

              {/* Overhead notice */}
              {overheadSections.length > 0 && (
                <div style={s.overheadBanner}>
                  Internal costs (${overheadTotal.toFixed(2)}) are spread into labor pricing automatically — your client will not see overhead as a separate line item.
                </div>
              )}

              {sections.length === 0 && <div style={s.emptySections}><div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div><div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>No sections yet</div><div style={{ color: '#9ca3af', fontSize: '13px' }}>Click "Add Section" below</div></div>}

              {sections.length > 0 && (
                <div style={s.colHeader}>
                  <span style={{ flex: 1 }}>Description</span>
                  <span style={{ width: '72px', textAlign: 'center' }}>Qty</span>
                  <span style={{ width: '90px', textAlign: 'right' }}>Your Cost</span>
                  <span style={{ width: '90px', textAlign: 'right' }}>Client $</span>
                  <span style={{ width: '28px' }}></span>
                </div>
              )}

              {sections.map((section, secIdx) => {
                const isOverhead = section.is_overhead
                const isOptional = section.client_optional

                return (
                  <div key={section._id} style={{ ...s.sectionBlock, border: isOverhead ? '1px solid #7c3aed40' : isOptional ? '1px solid #2563eb40' : '1px solid #e4ebe4' }}>
                    <div style={{ ...s.sectionBar, background: isOverhead ? '#f5f3ff' : isOptional ? '#eff6ff' : '#f2faf2' }}>
                      <div style={s.sectionBarLeft}>
                        <div style={{ ...s.sectionDot, background: isOverhead ? '#7c3aed' : isOptional ? '#2563eb' : '#2d6a2d' }} />
                        <input style={s.sectionNameInput} value={section.name} onChange={e => updateSection(section._id, 'name', e.target.value)} placeholder="Section name..." />
                        {isOverhead && <span style={s.overheadTag}>🔒 Internal Only</span>}
                        {!isOverhead && (
                          <label style={s.toggleLabel}>
                            <input type="checkbox" checked={section.client_optional} onChange={() => updateSection(section._id, 'client_optional', !section.client_optional)} />
                            <span style={{ fontSize: '11px', color: isOptional ? '#2563eb' : '#9ca3af' }}>Optional</span>
                          </label>
                        )}
                        {isOptional && (
                          <label style={s.toggleLabel}>
                            <input type="checkbox" checked={section.client_selected} onChange={() => updateSection(section._id, 'client_selected', !section.client_selected)} />
                            <span style={{ fontSize: '11px' }}>Include in total</span>
                          </label>
                        )}
                        <label style={s.toggleLabel}>
                          <input type="checkbox" checked={section.is_overhead || false} onChange={() => updateSection(section._id, 'is_overhead', !section.is_overhead)} />
                          <span style={{ fontSize: '11px', color: '#7c3aed' }}>Overhead</span>
                        </label>
                      </div>
                      <div style={s.sectionBarRight}>
                        <span style={{ ...s.sectionSubtotal, color: isOverhead ? '#7c3aed' : isOptional ? '#2563eb' : '#2d6a2d' }}>
                          {isOverhead ? `Overhead: $${sectionTrueCost(section).toFixed(2)} → $${overheadPerLaborHour.toFixed(2)}/hr` : `$${sectionClientTotal(section, overheadPerLaborHour).toFixed(2)}`}
                        </span>
                        <button style={s.iconBtn} onClick={() => moveSection(section._id, -1)} disabled={secIdx === 0}>↑</button>
                        <button style={s.iconBtn} onClick={() => moveSection(section._id, 1)} disabled={secIdx === sections.length - 1}>↓</button>
                        <button style={s.iconBtn} onClick={() => duplicateSection(section._id)} title="Duplicate">⧉</button>
                        <button style={s.removeSectionBtn} onClick={() => removeSection(section._id)}>Remove</button>
                      </div>
                    </div>

                    {/* Section description */}
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', background: isOverhead ? '#fffbf0' : '#eef8f2' }}>
                      {isOverhead
                        ? <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b3a08', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Internal section notes (not shown to client)</div>
                        : <div style={{ fontSize: '11px', fontWeight: '600', color: '#256942', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Client-facing description (shown on proposal)</div>
                      }
                      <textarea
                        style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: `1px solid ${isOverhead ? '#edd890' : '#a8d5bc'}`, fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', minHeight: '48px', background: 'white', color: '#374151', lineHeight: '1.5' }}
                        value={section.section_description || ''}
                        onChange={e => updateSection(section._id, 'section_description', e.target.value)}
                        placeholder={isOverhead ? 'Internal overhead notes...' : 'What will your client see for this section?'}
                      />
                    </div>

                    {section.items.map(item => {
                      // Always the same value itemClientPrice() will actually charge --
                      // detectType() is the single source of truth for both, so the
                      // badge can never show a type other than what's being billed.
                      const effectiveType = detectType(item, section.name)
                      const typeColors = { Material: { bg: '#f0f9ff', color: '#0369a1' }, Labor: { bg: '#f0fdf4', color: '#15803d' }, Equipment: { bg: '#fef3c7', color: '#92400e' }, Overhead: { bg: '#f5f3ff', color: '#7c3aed' }, Other: { bg: '#f9fafb', color: '#6b7c6b' } }
                      const tc = typeColors[effectiveType] || typeColors.Other
                      const itemPrice = itemClientPrice(item, section.name, overheadPerLaborHour)
                      const itemCost = itemTrueCost(item)
                      const itemMargin = itemPrice > 0 ? ((itemPrice - itemCost) / itemPrice * 100) : null

                      return (
                        <div key={item._id} style={{ ...s.lineItem, background: item.baked_in ? '#fffbeb' : 'white', opacity: item.baked_in ? 0.8 : 1 }}>
                          <div style={{ flex: 1, paddingRight: '8px' }}>
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <select style={{ ...s.typeBadge, background: tc.bg, color: tc.color, borderColor: tc.color + '40' }} value={effectiveType} onChange={e => updateItem(section._id, item._id, 'item_type', e.target.value)}>
                                <option value="Material">Material</option>
                                <option value="Labor">Labor</option>
                                <option value="Equipment">Equipment</option>
                                <option value="Overhead">Overhead</option>
                                <option value="Other">Other</option>
                              </select>
                              {item.baked_in && <span style={s.bakedBadgePill}>🔒 Baked In</span>}
                            </div>
                            <div style={{ marginBottom: '6px' }}>
                              <MaterialDropdown materials={materials} value={item.material_id} onChange={matId => updateItem(section._id, item._id, 'material_id', matId)} onAddNew={() => setShowAddMaterial(true)} />
                            </div>
                            <input style={s.descInput} value={item.description} onChange={e => updateItem(section._id, item._id, 'description', e.target.value)} placeholder="Description..." />
                            <input style={s.noteInput} value={item.notes} onChange={e => updateItem(section._id, item._id, 'notes', e.target.value)} placeholder="Internal note (never on PDF)..." />
                            <div style={s.metaStrip}>
                              <select style={s.metaSelect} value={item.unit} onChange={e => updateItem(section._id, item._id, 'unit', e.target.value)}>
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                              {!item.baked_in && effectiveType !== 'Labor' && effectiveType !== 'Overhead' && (
                                <>
                                  <select style={s.metaSelect} value={item.markup_type === 'custom' ? 'custom' : String(item.markup_pct)} onChange={e => { if (e.target.value === 'custom') updateItem(section._id, item._id, 'markup_type', 'custom'); else { updateItem(section._id, item._id, 'markup_type', 'preset'); updateItem(section._id, item._id, 'markup_pct', parseFloat(e.target.value)) } }}>
                                    {MARKUP_OPTIONS.map(o => <option key={o.label} value={String(o.value)}>{o.label}</option>)}
                                  </select>
                                  {item.markup_type === 'custom' && <input style={{ ...s.metaSelect, width: '52px', textAlign: 'center' }} type="number" min="0" value={item.custom_markup} onChange={e => updateItem(section._id, item._id, 'custom_markup', e.target.value)} placeholder="%" />}
                                </>
                              )}
                              {effectiveType === 'Labor' && <span style={{ fontSize: '11px', color: '#15803d', background: '#f0fdf4', padding: '3px 8px', borderRadius: '4px' }}>No markup on labor</span>}
                              {!isOverhead && (
                                <button style={{ ...s.bakeBtn, background: item.baked_in ? '#fef08a' : '#f3f4f6', color: item.baked_in ? '#78350f' : '#374151', border: `1px solid ${item.baked_in ? '#fde047' : '#e5e7eb'}`, fontWeight: item.baked_in ? '700' : '500' }} onClick={() => updateItem(section._id, item._id, 'baked_in', !item.baked_in)} title="Bake In: hide line item from client but include its price in section total">
                                  {item.baked_in ? '🔒 Baked In' : 'Bake In'}
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={{ width: '72px', paddingRight: '8px' }}>
                            <input style={{ ...s.numInput, textAlign: 'center' }} type="number" min="0" step="0.01" value={item.qty} onChange={e => updateItem(section._id, item._id, 'qty', e.target.value)} />
                          </div>
                          <div style={{ width: '90px', paddingRight: '8px' }}>
                            <input style={{ ...s.numInput, textAlign: 'right' }} type="number" min="0" step="0.01" value={item.unit_cost} onChange={e => updateItem(section._id, item._id, 'unit_cost', e.target.value)} placeholder="0.00" />
                            {item.unit_cost && <div style={{ fontSize: '10px', color: '#9ca3af', textAlign: 'right', marginTop: '2px' }}>cost: ${itemCost.toFixed(2)}</div>}
                            {item.unit_cost && itemMargin !== null && <div style={{ fontSize: '10px', textAlign: 'right', marginTop: '1px', fontWeight: itemMargin < 35 ? '700' : '500', color: itemMargin < 35 ? '#dc2626' : '#9ca3af' }}>{itemMargin.toFixed(0)}% margin</div>}
                          </div>
                          <div style={{ width: '90px', textAlign: 'right', paddingTop: '8px' }}>
                            {isOverhead ? (
                              <div style={{ fontWeight: '600', color: '#7c3aed', fontSize: '13px' }}>Overhead<div style={{ fontSize: '10px' }}>${itemCost.toFixed(2)}</div></div>
                            ) : (
                              <>
                                <div style={{ fontWeight: '700', color: item.baked_in ? '#78350f' : '#1a2e1a', fontSize: '15px' }}>${itemPrice.toFixed(2)}</div>
                                {item.baked_in && <div style={{ fontSize: '10px', color: '#78350f' }}>baked</div>}
                              </>
                            )}
                          </div>
                          <div style={{ width: '28px', display: 'flex', justifyContent: 'center', paddingTop: '8px' }}>
                            <button style={s.removeItemBtn} onClick={() => removeItem(section._id, item._id)}>✕</button>
                          </div>
                        </div>
                      )
                    })}

                    <div style={s.addItemRow}>
                      <button style={s.addItemBtn} onClick={() => addItem(section._id)}>+ Add Item</button>
                      <div style={{ fontSize: '12px', color: '#374151' }}>
                        {isOverhead
                          ? <span style={{ color: '#7c3aed' }}>Overhead: ${sectionTrueCost(section).toFixed(2)} spread as ${overheadPerLaborHour.toFixed(2)}/labor hr</span>
                          : <>Total: <strong>${sectionClientTotal(section, overheadPerLaborHour).toFixed(2)}</strong>{section.items.some(i => i.baked_in) && <span style={{ color: '#78350f', marginLeft: '8px' }}>{section.items.filter(i => i.baked_in).length} baked</span>}</>
                        }
                      </div>
                    </div>
                  </div>
                )
              })}

              <div style={{ marginTop: '12px' }}>
                {!showSectionPicker ? (
                  <button style={s.addSectionBtn} onClick={() => setShowSectionPicker(true)}>+ Add Section</button>
                ) : (
                  <div style={s.sectionPickerCard}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>Choose a preset or enter a custom name</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                      {SECTION_PRESETS.map(p => <button key={p} style={s.presetBtn} onClick={() => addSection(p)}>{p}</button>)}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input style={{ ...s.input, flex: 1 }} value={customSectionName} onChange={e => setCustomSectionName(e.target.value)} placeholder="Custom section name..." autoFocus onKeyDown={e => e.key === 'Enter' && addSection(customSectionName)} />
                      <button style={s.saveDraftBtn} onClick={() => addSection(customSectionName)}>Add</button>
                      <button style={s.pdfBtn} onClick={() => { setShowSectionPicker(false); setCustomSectionName('') }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div style={s.section}>
              <div style={s.sectionTitle}>Terms & Notes</div>
              <div style={{ ...s.notesGrid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
                <div><label style={s.label}>Client Notes (shown on proposal)</label><textarea style={{ ...s.input, minHeight: '80px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} value={clientNotes} onChange={e => setClientNotes(e.target.value)} placeholder="Notes the client will see..." /></div>
                <div><label style={s.label}>Internal Notes (never shown to client)</label><textarea style={{ ...s.input, minHeight: '80px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} value={internalNotes} onChange={e => setInternalNotes(e.target.value)} placeholder="Internal reminders, cost flags..." /></div>
              </div>
            </div>

            {/* Payment Terms — its own callout, not buried in Terms & Notes, since it's
                what actually gets quoted to the client and previously got lost on save
                (see Docs/DECISIONS_LOG.md). */}
            <div style={{ ...s.section, background: '#eef8f2', border: '1.5px solid #a8d5bc', borderRadius: '10px' }}>
              <div style={{ ...s.sectionTitle, color: '#1e4d34' }}>💳 Payment Terms</div>
              <label style={{ ...s.label, marginBottom: '6px' }}>Shown on the proposal — leave blank for the standard default</label>
              <textarea
                style={{ ...s.input, minHeight: '60px', resize: 'vertical', width: '100%', boxSizing: 'border-box', background: 'white' }}
                value={paymentTerms}
                onChange={e => setPaymentTerms(e.target.value)}
                placeholder="e.g. 15% due at signing, 35% at job start, 50% due on completion..."
              />
            </div>

          </div>

          {/* Summary Panel */}
          <div style={s.rightCol}>
            <div style={s.summaryPanel}>
              <div style={s.summaryHeader}>Estimate Summary</div>

              {baseSections.map(sec => (
                <div key={sec._id} style={s.summaryLineRow}>
                  <span style={s.summaryLineLabel}>{sec.name}</span>
                  <span style={s.summaryLineVal}>${sectionClientTotal(sec, overheadPerLaborHour).toFixed(2)}</span>
                </div>
              ))}

              {optionalSelected.length > 0 && (
                <>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '8px 0 4px' }}>Optional</div>
                  {optionalSelected.map(sec => (
                    <div key={sec._id} style={s.summaryLineRow}>
                      <span style={{ ...s.summaryLineLabel, color: '#2563eb' }}>{sec.name}</span>
                      <span style={{ ...s.summaryLineVal, color: '#2563eb' }}>+${sectionClientTotal(sec, overheadPerLaborHour).toFixed(2)}</span>
                    </div>
                  ))}
                </>
              )}

              {overheadTotal > 0 && (
                <div style={{ ...s.summaryLineRow, opacity: 0.6, fontSize: '11px' }}>
                  <span style={{ color: '#7c3aed' }}>🔒 Overhead (internal)</span>
                  <span style={{ color: '#7c3aed' }}>${overheadTotal.toFixed(2)}</span>
                </div>
              )}

              <div style={s.summaryDivider} />
              <div style={s.summaryLineRow}><span style={s.summaryLineLabel}>Base Subtotal</span><span style={s.summaryLineVal}>${baseSubtotal.toFixed(2)}</span></div>
              {optionalSubtotal > 0 && <div style={s.summaryLineRow}><span style={{ ...s.summaryLineLabel, color: '#2563eb' }}>+ Optional</span><span style={{ ...s.summaryLineVal, color: '#2563eb' }}>${optionalSubtotal.toFixed(2)}</span></div>}

              <div style={{ ...s.summaryLineRow, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: '#374151', flex: 1 }}>
                  <input type="checkbox" checked={applyTax} onChange={e => setApplyTax(e.target.checked)} />Tax (8.25%)
                </label>
                <span style={s.summaryLineVal}>{applyTax ? `$${tax.toFixed(2)}` : '—'}</span>
              </div>

              <div style={s.summaryTotalRow}>
                <span style={s.summaryTotalLabel}>Total</span>
                <span style={s.summaryTotalVal}>${grandTotal.toFixed(2)}</span>
              </div>

              {optionalSelected.length === 0 && sections.some(s => s.client_optional) && (
                <div style={{ fontSize: '11px', color: '#2563eb', background: '#eff6ff', padding: '6px 10px', borderRadius: '6px', marginBottom: '8px' }}>
                  + Optional add-ons available: ${sections.filter(s => s.client_optional).reduce((t, sec) => t + sectionClientTotal(sec, overheadPerLaborHour), 0).toFixed(2)}
                </div>
              )}

              <div style={s.depositBlock}>
                <label style={s.label}>Deposit %</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                  <input style={{ ...s.input, flex: 1, textAlign: 'center' }} type="number" min="0" max="100" value={depositPct} onChange={e => setDepositPct(e.target.value)} />
                  <span style={{ fontWeight: '700', color: '#2d6a2d', fontSize: '15px' }}>${depositAmt.toFixed(2)}</span>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7c6b' }}>Balance: ${balanceDue.toFixed(2)}</div>
              </div>

              <div style={s.summaryMeta}>
                <div style={s.metaInfoRow}><span style={s.metaInfoLabel}>Status</span><span style={{ ...s.statusBadge, background: status === 'draft' ? '#fef3c7' : status === 'sent' ? '#dbeafe' : status === 'approved' ? '#dcfce7' : '#fee2e2', color: status === 'draft' ? '#92400e' : status === 'sent' ? '#1d4ed8' : status === 'approved' ? '#15803d' : '#dc2626' }}>{status}</span></div>
                <div style={s.metaInfoRow}><span style={s.metaInfoLabel}>Gross Margin</span><span style={{ ...s.metaInfoVal, color: actualMargin >= 35 ? '#15803d' : '#f59e0b', fontWeight: '700' }}>{actualMargin.toFixed(1)}%</span></div>
                <div style={s.metaInfoRow}><span style={s.metaInfoLabel}>Valid Until</span><span style={s.metaInfoVal}>{expiryDate ? new Date(expiryDate + 'T12:00:00').toLocaleDateString() : '—'}</span></div>
              </div>

              <div style={s.summaryActions}>
                <button style={s.pdfBtnFull} onClick={printPDF}>🖨 Preview PDF</button>
                <button style={s.downloadBtnFull} onClick={downloadPDF}>⬇ Download PDF</button>
                <button style={s.convertBtn} onClick={convertToJob}>→ Convert to Job</button>
                <button style={s.payDepositBtn}>💳 Pay Deposit · ${depositAmt.toFixed(2)}<br /><span style={{ fontSize: '10px', opacity: 0.7 }}>Stripe coming soon</span></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page: { width: '100%', paddingBottom: '60px' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  topBarRight: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '22px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '2px 0 0', color: '#6b7c6b', fontSize: '13px' },
  backBtn: { background: 'none', border: '1.5px solid #d1d5db', color: '#374151', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  pdfBtn: { background: 'white', border: '1.5px solid #d1d5db', color: '#374151', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  downloadBtn: { background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  generateProposalBtn: { background: '#eff6ff', border: '1.5px solid #93c5fd', color: '#1d4ed8', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  saveDraftBtn: { background: 'white', border: '1.5px solid #2d6a2d', color: '#2d6a2d', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  sendNowBtn: { background: '#2d6a2d', color: 'white', border: 'none', padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '14px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dismissBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '16px', fontFamily: 'inherit' },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', color: '#16a34a', fontSize: '14px', marginBottom: '16px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '20px', background: 'white', borderRadius: '10px', padding: '4px', border: '1px solid #e4ebe4', width: 'fit-content' },
  tab: { padding: '8px 18px', borderRadius: '7px', border: 'none', background: 'none', color: '#6b7c6b', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' },
  tabActive: { background: '#2d6a2d', color: 'white', fontWeight: '600' },
  metricsPill: { background: '#f0fdf4', color: '#15803d', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', alignItems: 'flex-start' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 },
  rightCol: { position: 'sticky', top: '20px' },
  section: { background: 'white', borderRadius: '12px', padding: '22px 24px', border: '1px solid #e4ebe4', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: '15px', fontWeight: '700', color: '#1a2e1a', marginBottom: '16px' },
  clientGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  clientAutoFill: { display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '6px', padding: '8px 12px', background: '#f2faf2', borderRadius: '6px', border: '1px solid #d1e7d1' },
  clientDetail: { fontSize: '12px', color: '#2d6a2d', fontWeight: '500' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  toolbar: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', background: 'white', borderRadius: '10px', padding: '12px 16px', border: '1px solid #e4ebe4', position: 'relative' },
  toolBtn: { background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151', padding: '7px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  dropdown: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: '240px' },
  dropdownTitle: { padding: '10px 14px 6px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' },
  dropdownEmpty: { padding: '14px', fontSize: '13px', color: '#9ca3af', textAlign: 'center' },
  dropdownRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderTop: '1px solid #f3f4f6', gap: '8px' },
  dropdownLabel: { fontSize: '14px', color: '#374151', flex: 1 },
  dropdownBtn: { background: 'none', border: '1px solid #e5e7eb', color: '#374151', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' },
  visRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderTop: '1px solid #f3f4f6', cursor: 'pointer' },
  addMaterialQuickBtn: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  optionalBanner: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' },
  overheadBanner: { background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', color: '#7c3aed', fontWeight: '500' },
  colHeader: { display: 'flex', padding: '8px 14px', background: '#f9fafb', borderRadius: '6px', fontSize: '10px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px', gap: '8px' },
  emptySections: { textAlign: 'center', padding: '40px 20px', color: '#9ca3af' },
  sectionBlock: { marginBottom: '8px', borderRadius: '10px', overflow: 'hidden' },
  sectionBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e4ebe4', flexWrap: 'wrap', gap: '8px' },
  sectionBarLeft: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 },
  sectionBarRight: { display: 'flex', alignItems: 'center', gap: '6px' },
  sectionDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  sectionNameInput: { fontWeight: '700', fontSize: '14px', color: '#1a2e1a', border: 'none', background: 'transparent', outline: 'none', fontFamily: 'inherit', flex: 1, minWidth: '80px' },
  sectionSubtotal: { fontWeight: '700', fontSize: '13px', marginRight: '4px' },
  overheadTag: { fontSize: '10px', background: '#ede9fe', color: '#7c3aed', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' },
  iconBtn: { background: 'none', border: '1px solid #e5e7eb', color: '#6b7c6b', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontFamily: 'inherit' },
  removeSectionBtn: { background: 'none', border: '1px solid #fecaca', color: '#dc2626', padding: '2px 8px', borderRadius: '5px', cursor: 'pointer', fontSize: '11px', fontWeight: '500', fontFamily: 'inherit' },
  lineItem: { display: 'flex', alignItems: 'flex-start', padding: '14px 14px', borderBottom: '1px solid #f9fafb', gap: '8px' },
  typeBadge: { padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', border: '1px solid', cursor: 'pointer', fontFamily: 'inherit', appearance: 'none', WebkitAppearance: 'none' },
  bakedBadgePill: { fontSize: '11px', color: '#78350f', background: '#fef08a', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' },
  descInput: { width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1.5px solid #e5e7eb', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '4px' },
  noteInput: { width: '100%', padding: '4px 10px', borderRadius: '5px', border: '1px solid #f0f0f0', fontSize: '11px', fontFamily: 'inherit', boxSizing: 'border-box', background: '#fafafa', color: '#9ca3af', marginBottom: '6px' },
  metaStrip: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  metaSelect: { padding: '4px 7px', borderRadius: '5px', border: '1px solid #e5e7eb', fontSize: '12px', fontFamily: 'inherit', background: '#fafafa' },
  bakeBtn: { padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', transition: 'all 0.15s' },
  numInput: { width: '100%', padding: '8px 8px', borderRadius: '7px', border: '1.5px solid #e5e7eb', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' },
  removeItemBtn: { background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '15px', padding: '2px', fontFamily: 'inherit' },
  addItemRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fafafa' },
  addItemBtn: { background: 'none', border: '1px dashed #d1d5db', color: '#6b7c6b', padding: '5px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
  addSectionBtn: { width: '100%', padding: '14px', border: '2px dashed #d1d5db', borderRadius: '10px', background: 'none', color: '#6b7c6b', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  sectionPickerCard: { background: '#f9fafb', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb', marginTop: '8px' },
  presetBtn: { background: 'white', border: '1px solid #e5e7eb', color: '#374151', padding: '5px 12px', borderRadius: '16px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: '500' },
  notesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  summaryPanel: { background: 'white', borderRadius: '12px', border: '1px solid #e4ebe4', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  summaryHeader: { fontSize: '15px', fontWeight: '700', color: '#1a2e1a', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #f0f0f0' },
  summaryLineRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '13px' },
  summaryLineLabel: { color: '#374151' },
  summaryLineVal: { color: '#1a2e1a', fontWeight: '500' },
  summaryDivider: { height: '1px', background: '#f0f0f0', margin: '8px 0' },
  summaryTotalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: '2px solid #1a2e1a', marginTop: '6px' },
  summaryTotalLabel: { fontSize: '16px', fontWeight: '700', color: '#1a2e1a' },
  summaryTotalVal: { fontSize: '22px', fontWeight: '800', color: '#2d6a2d' },
  depositBlock: { background: '#f9fafb', borderRadius: '8px', padding: '12px', margin: '12px 0', border: '1px solid #e5e7eb' },
  summaryMeta: { borderTop: '1px solid #f0f0f0', paddingTop: '10px', marginTop: '4px' },
  metaInfoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '12px' },
  metaInfoLabel: { color: '#9ca3af' },
  metaInfoVal: { color: '#374151', fontWeight: '500' },
  statusBadge: { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' },
  summaryActions: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '14px' },
  pdfBtnFull: { background: '#f3f4f6', border: '1.5px solid #d1d5db', color: '#374151', padding: '9px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
  downloadBtnFull: { background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', padding: '9px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
  convertBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
  payDepositBtn: { background: '#1a2e1a', color: 'white', borderRadius: '8px', padding: '10px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', opacity: 0.7, cursor: 'not-allowed', width: '100%', boxSizing: 'border-box', border: 'none', fontFamily: 'inherit', lineHeight: '1.4' },
}